/**
 * A tela de Congregação: as duas folhas do mês, do jeito que elas são no papel.
 *
 * **Esta tela é o relatório.** O Relatório de Movimentação em PDF é ela mesma, fotografada
 * por um Chromium dentro do servidor. Por isso ela não tem nada que só sirva para a tela sem
 * estar marcado com `so-tela`, e por isso o papel vem antes dos botões na ordem do código.
 * Desenhar uma segunda versão da folha só para o PDF seria criar duas verdades que um dia se
 * desencontram, e é o que a decisão 3 da parte 3 não quer.
 *
 * **É só leitura.** Nenhum número daqui se digita. Quem lança é o notebook, e o painel é onde
 * o pastor confere. Uma célula editável aqui seria uma segunda porta para o mesmo dinheiro.
 *
 * A marcação das folhas é a do notebook, copiada de `FolhaOfertas.tsx` e `FolhaDizimos.tsx`,
 * com as larguras de coluna medidas no papel que estão comentadas lá.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  alcanca, ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type FolhasDaCongregacao, type LinhaDeDizimo
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { competenciaPorExtenso, emReais, mesAnterior, mesSeguinte, NOME_DA_SITUACAO } from '../dinheiro'
import {
  ehParaImprimir, enderecoDaCongregacao, enderecoDasSaidasDaCongregacao, enderecoDaTelaDaCongregacao,
  enderecoDoFechamentoDaCongregacao, irPara
} from '../rotas'

/** As quatro formas de oferta, na ordem das colunas do papel. */
const COLUNAS_DE_OFERTA = [
  { chave: 'cartao', titulo: 'CARTÃO' },
  { chave: 'pix', titulo: 'PIX' },
  { chave: 'especie', titulo: 'ESPÉCIE' },
  { chave: 'moeda', titulo: 'MOEDA' }
] as const

/** As três formas de dízimo, na ordem das colunas do papel. */
const COLUNAS_DE_DIZIMO = [
  { chave: 'CARTAO', titulo: 'CARTÃO' },
  { chave: 'PIX_TRANSF', titulo: 'PIX/TRANSF.' },
  { chave: 'ESPECIE', titulo: 'ESPEC.' }
] as const

/** `2026-08-05` vira `05/08/2026`, como no notebook. */
function porExtenso(data: string): string {
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

/**
 * O dia que o endereço pede, quando ele veio do cartão "Hoje": `#dia-2026-10-11`.
 *
 * Lido na hora, e não guardado numa constante do módulo: ao andar de mês o endereço troca SEM o
 * pedaço depois do `#`, e a folha tem de parar de procurar aquele dia.
 */
function diaDoEndereco(): string | null {
  return /^#dia-(\d{4}-\d{2}-\d{2})$/.exec(window.location.hash)?.[1] ?? null
}

export function Congregacao({
  eu,
  id,
  competenciaDoEndereco,
  aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const [dados, setDados] = useState<FolhasDaCongregacao | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const paraImprimir = ehParaImprimir()

  useEffect(() => {
    let vale = true
    setErro(null)
    setDados(null)

    servidor
      .congregacao(id, competenciaDoEndereco)
      .then((recebido) => {
        if (vale) setDados(recebido)
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [id, competenciaDoEndereco, aoSair])

  /**
   * Quem vem do cartão "Hoje" do Início cai na linha daquele dia, e não no alto da folha.
   *
   * O sinal é o `#dia-2026-10-11` no endereço. O `#hoje` antigo continua valendo, porque link
   * guardado não pode parar de funcionar, e ele quer dizer a linha que o SERVIDOR marcou como
   * hoje. Procura-se pela CLASSE, e não por um `id`, porque as duas folhas podem ter linha do
   * mesmo dia, e dois elementos com o mesmo `id` na página é defeito esperando acontecer.
   *
   * **Nunca rola na hora de fotografar**: o PDF é a folha inteira, e rolar não muda o papel, mas
   * `scrollIntoView` com animação atrasaria o sinal `data-pronto` que o Chromium espera.
   */
  useEffect(() => {
    if (dados === null || paraImprimir) return

    const dia = diaDoEndereco()
    if (dia === null && window.location.hash !== '#hoje') return

    const linha = document.querySelector(
      dia === null ? 'tr.hoje' : `tr[data-dia="${dia}"]`
    )
    if (linha) linha.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [dados, paraImprimir])

  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaCongregacao(id, para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />
      )}

      {/*
        `data-pronto` é o sinal para o Chromium que fotografa esta tela: ele espera por ele
        antes de imprimir. Sem um sinal explícito, a foto sairia da tela "Carregando...", e o
        PDF do mês viria em branco sem ninguém perceber.
      */}
      <main
        className="folha folha-lancamento"
        data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}
      >
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {/*
              O cabeçalho do papel. Ele existe na tela também, e não só na impressão: o pastor
              que abre a folha no celular precisa saber de qual congregação e de qual mês ela é
              sem ter que lembrar em que botão clicou.
            */}
            <header className="cabecalho-da-folha">
              <h1>{dados.congregacao.nome}</h1>
              <p className="subtitulo">
                Movimentação de {dados.competencia_por_extenso}
                <span className={`situacao ${dados.situacao}`}>
                  {NOME_DA_SITUACAO[dados.situacao] ?? dados.situacao}
                </span>
              </p>
              {dados.fechamento.versao !== null && dados.fechamento.assinado_por !== null && (
                <p className="assinatura">
                  Assinado por {dados.fechamento.assinado_por}
                  {dados.fechamento.assinado_em ? `, em ${porExtenso(dados.fechamento.assinado_em.slice(0, 10))}` : ''}
                  {dados.fechamento.versao > 1 ? ` · versão ${dados.fechamento.versao}` : ''}
                </p>
              )}
              {dados.fechamento.motivo_da_reabertura !== null && (
                <p className="assinatura">Reaberto: {dados.fechamento.motivo_da_reabertura}</p>
              )}
            </header>

            {mes !== null && !paraImprimir && (
              <nav className="navegar-mes so-tela">
                <button aria-label="Mês anterior" onClick={() => andar(mesAnterior(mes))}>◀</button>
                <strong>{competenciaPorExtenso(mes)}</strong>
                <button aria-label="Mês seguinte" onClick={() => andar(mesSeguinte(mes))}>▶</button>
                <a
                  className="baixar"
                  href={`/v1/painel/congregacoes/${encodeURIComponent(id)}/movimentacao.pdf?competencia=${mes}`}
                >
                  Baixar o relatório em PDF
                </a>
              </nav>
            )}

            <FolhaDeOfertas dados={dados} />
            <FolhaDeDizimos dados={dados} />

            {/*
              O caminho para a folha de SAÍDAS do mesmo mês.

              Ele fica DEPOIS das duas folhas, e com `so-tela`, por um motivo só: esta tela é o
              documento que o servidor fotografa no Relatório de Movimentação, e um botão no meio
              do papel não é papel. As saídas têm tela própria pela mesma razão.
            */}
            {!paraImprimir && (
              <p className="totais so-tela">
                <button
                  className="voltar"
                  onClick={() => irPara(enderecoDasSaidasDaCongregacao(id, mes))}
                >
                  Ver as saídas de {dados.competencia_por_extenso} ▶
                </button>
                {' '}
                <button
                  className="voltar"
                  onClick={() => irPara(enderecoDoFechamentoDaCongregacao(id, mes))}
                >
                  Ver o fechamento de {dados.competencia_por_extenso} ▶
                </button>
                {/* A fase 6 do espelho: cada atalho só para quem abre a tela daquela congregação. */}
                {alcanca(eu.alcance_das_telas?.dizimistas, id) && (
                  <>
                    {' '}
                    <button className="voltar" onClick={() => irPara(enderecoDaTelaDaCongregacao('dizimistas', id, mes))}>
                      Ver os dizimistas ▶
                    </button>
                  </>
                )}
                {alcanca(eu.alcance_das_telas?.repasses, id) && (
                  <>
                    {' '}
                    <button className="voltar" onClick={() => irPara(enderecoDaTelaDaCongregacao('repasses', id, mes))}>
                      Ver os repasses ▶
                    </button>
                  </>
                )}
                {alcanca(eu.alcance_das_telas?.agenda, id) && (
                  <>
                    {' '}
                    <button className="voltar" onClick={() => irPara(enderecoDaTelaDaCongregacao('agenda', id, mes))}>
                      Ver a agenda de cultos ▶
                    </button>
                  </>
                )}
              </p>
            )}
          </>
        )}
      </main>
    </>
  )
}

/**
 * A folha de ofertas: uma linha por culto do mês.
 *
 * As larguras são as medidas no papel pelo notebook, e o comentário delas está lá, em
 * `FolhaOfertas.tsx`. A linha zerada fica: a folha é a folha de papel, com os cultos já
 * impressos, esperando o valor. Tirar as zeradas faria o mês parecer ter tido menos cultos.
 */
function FolhaDeOfertas({ dados }: { dados: FolhasDaCongregacao }): JSX.Element {
  const { linhas, totais, de_departamento: deDepartamento } = dados.ofertas

  return (
    <section className="folha-de-um-tipo">
      <h2>LANÇAMENTO DE OFERTAS – MÊS: {dados.competencia_por_extenso.toUpperCase()}</h2>
      {/* Some na tela grande, onde a folha cabe inteira, e some no papel. */}
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>

      <table className="grade folha-de-ofertas">
        {/* As larguras medidas no papel pelo notebook, em `FolhaOfertas.tsx`. */}
        <colgroup>
          <col style={{ width: '11%' }} /><col style={{ width: '13%' }} /><col style={{ width: '13%' }} />
          <col style={{ width: '12.5%' }} /><col style={{ width: '12.5%' }} /><col style={{ width: '12.5%' }} />
          <col style={{ width: '12.5%' }} /><col style={{ width: '13%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="rotulo">DATA</th><th className="rotulo">CULTO</th><th className="rotulo">DIA</th>
            {COLUNAS_DE_OFERTA.map((c) => <th key={c.chave} className="valor">{c.titulo}</th>)}
            <th className="valor">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <td colSpan={8} className="lapis">
                Nenhum culto na agenda deste mês. A folha de ofertas nasce da agenda do notebook.
              </td>
            </tr>
          )}

          {linhas.map((l) => (
            // A linha de hoje fica marcada. Quem vem do cartão "Hoje" do Início cai nela.
            <tr
              key={l.culto_id}
              data-dia={l.data}
              className={l.data === dados.hoje ? 'hoje' : undefined}
            >
              <td className="impresso data">{porExtenso(l.data)}</td>
              <td className="impresso">
                {l.culto ?? ''}
                {/* O culto com destino não fica no livro da congregação. Dizer isso na linha
                    evita a conta "não bate" que o pastor faria de cabeça. */}
                {l.do_departamento && <small className="destino"> do departamento</small>}
              </td>
              <td className="impresso">{l.dia_semana}</td>
              {COLUNAS_DE_OFERTA.map((c) => (
                <td key={c.chave} className="valor manuscrito">{l[c.chave] ? emReais(l[c.chave]) : ''}</td>
              ))}
              <td className="valor total">{l.total ? emReais(l.total) : ''}</td>
            </tr>
          ))}

          {linhas.length > 0 && (
            <tr className="rodape">
              <td colSpan={3}>TOTAL DO MÊS</td>
              {COLUNAS_DE_OFERTA.map((c) => <td key={c.chave} className="valor">{emReais(totais[c.chave])}</td>)}
              <td className="valor">{emReais(totais.total)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/*
        O total escrito por extenso, embaixo da folha.

        No celular a tabela rola para o lado, e a coluna TOTAL é a última: o número mais
        importante da folha seria o único que exige arrastar para ver. Aqui ele fica parado, e
        no papel ele não atrapalha ninguém.
      */}
      {linhas.length > 0 && (
        <p className="explicacao">
          Total do mês: <strong>{emReais(totais.total)}</strong> ·{' '}
          {linhas.length === 1 ? '1 culto na agenda' : `${linhas.length} cultos na agenda`}.
          {deDepartamento > 0 && (
            <>
              {' '}Desse total, <strong>{emReais(deDepartamento)}</strong> é de culto com destino
              de departamento, e não fica no livro desta congregação.
            </>
          )}
        </p>
      )}
    </section>
  )
}

/**
 * A folha de dízimos e votos, uma linha por lançamento.
 *
 * O estorno aparece, com o valor negativo, e não some: o papel registra o que aconteceu, e
 * não o resultado líquido. Quem conferir a folha contra o envelope precisa achar as duas
 * linhas.
 */
function FolhaDeDizimos({ dados }: { dados: FolhasDaCongregacao }): JSX.Element {
  const { linhas, total_dizimos: totalDizimos, total_votos: totalVotos, total } = dados.dizimos

  return (
    <section className="folha-de-um-tipo">
      <h2>LANÇAMENTOS DE DÍZIMOS E VOTOS – {dados.competencia_por_extenso.toUpperCase()}</h2>
      {/* Some na tela grande, onde a folha cabe inteira, e some no papel. */}
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>

      <table className="grade">
        {/*
          As larguras são as que o notebook MEDIU no papel, e não chute. Lá a tabela tem oito
          colunas e a última, só de botões, some na impressão; aqui ela não existe, então as
          outras sete foram reescalonadas na mesma proporção.

          A coluna VALOR é a que manda: ela leva a fonte de máquina de escrever, que é mais
          larga, e precisa caber "R$ 100.000,00". Na primeira versão desta tela ela tinha 12% e
          "R$ 1.234,00" saiu quebrado em duas linhas no PDF, com o último zero embaixo. Foi
          exatamente o defeito que o notebook já tinha medido para não ter, num papel que vai
          para o arquivo da tesouraria.
        */}
        <colgroup>
          <col style={{ width: '11.5%' }} /><col style={{ width: '27%' }} /><col style={{ width: '9.4%' }} />
          <col style={{ width: '10.4%' }} /><col style={{ width: '13.5%' }} /><col style={{ width: '9.4%' }} />
          <col style={{ width: '18.8%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>DATA</th><th>NOME</th><th>TIPO</th>
            {COLUNAS_DE_DIZIMO.map((f) => <th key={f.chave} className="centro">{f.titulo}</th>)}
            <th className="valor">VALOR</th>
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <td colSpan={7} className="lapis">Nenhum dízimo ou voto lançado neste mês.</td>
            </tr>
          )}

          {linhas.map((l) => (
            <LinhaDoDizimo key={l.id} linha={l} ehDeHoje={l.data === dados.hoje} />
          ))}

          {linhas.length > 0 && (
            <tr className="rodape">
              <td colSpan={6}>TOTAL</td>
              <td className="valor">{emReais(total)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {linhas.length > 0 && (
        <p className="explicacao">
          Dízimos: {emReais(totalDizimos)} · Votos: {emReais(totalVotos)} ·{' '}
          {linhas.length === 1 ? '1 lançamento' : `${linhas.length} lançamentos`}. Os dízimos entram
          no total do mês pela data, e não dependem de haver culto na agenda naquele dia.
        </p>
      )}
    </section>
  )
}

function LinhaDoDizimo(
  { linha, ehDeHoje }: { linha: LinhaDeDizimo; ehDeHoje: boolean }
): JSX.Element {
  return (
    <tr
      data-dia={linha.data}
      className={[linha.e_estorno ? 'estorno' : '', ehDeHoje ? 'hoje' : ''].join(' ').trim() || undefined}
    >
      <td className="impresso data">{porExtenso(linha.data)}</td>
      <td className="manuscrito">{linha.nome}</td>
      <td className="impresso">{linha.tipo === 'VOTO' ? 'Voto' : 'Dízimo'}</td>
      {COLUNAS_DE_DIZIMO.map((f) => (
        <td key={f.chave} className="centro manuscrito">{linha.forma === f.chave ? 'X' : ''}</td>
      ))}
      <td className="valor manuscrito">{emReais(linha.valor)}</td>
    </tr>
  )
}
